import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Siren, Phone, Landmark, ShieldCheck, Scroll, Home, Users,
    FileText, Lock, BadgeAlert,
} from "lucide-react";

export default function EmergencyAccess() {
    const { token } = useParams();
    const [pin, setPin] = useState("");
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const verify = async (e) => {
        e.preventDefault();
        if (!pin || pin.length < 4) { setErr("Enter the 4-digit PIN."); return; }
        setErr(""); setBusy(true);
        try {
            const { data } = await axios.post(`${API}/crisis/public/${token}`, { pin });
            setData(data);
        } catch (e2) {
            setErr(e2.response?.data?.detail || e2.message);
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="crisis-mode min-h-screen bg-background text-foreground flex items-center justify-center px-6">
                <form onSubmit={verify} className="w-full max-w-sm" data-testid="emergency-form">
                    <div className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-primary">
                        <Siren className="h-4 w-4" /> Crisis Mode · Read-only
                    </div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Enter the 4-digit PIN</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        The owner of this vault shared a one-time link with you. Enter the PIN they sent separately.
                    </p>
                    <div className="mt-8 space-y-3">
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text" inputMode="numeric" maxLength={4}
                                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                                placeholder="0000"
                                data-testid="emergency-pin"
                                className="pl-9 font-mono tracking-[0.4em] text-center text-lg"
                                autoFocus
                            />
                        </div>
                        {err ? <div data-testid="emergency-error" className="text-sm text-destructive">{err}</div> : null}
                        <Button type="submit" disabled={busy} data-testid="emergency-submit" className="w-full rounded-full">
                            {busy ? "Verifying…" : "Open emergency access"}
                        </Button>
                    </div>
                    <p className="mt-8 text-xs text-muted-foreground">
                        Each access is logged. Be respectful — this view contains sensitive personal information.
                    </p>
                </form>
            </div>
        );
    }

    return (
        <div className="crisis-mode min-h-screen bg-background text-foreground" data-testid="emergency-view">
            <header className="border-b border-border">
                <div className="px-8 py-6 flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-primary">
                        <Siren className="h-4 w-4" /> Emergency view · {data.share?.label || "Family"}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                        valid until {data.share?.expires_at?.slice(0, 10)}
                    </div>
                </div>
            </header>
            <div className="px-8 py-10 space-y-10 max-w-6xl mx-auto">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">
                        Owner of this vault
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{data.owner?.name || "—"}</h1>
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                        {data.owner?.phone ? <Item icon={Phone} label="Phone" value={data.owner.phone} /> : null}
                        {data.owner?.blood_group ? <Item icon={BadgeAlert} label="Blood" value={data.owner.blood_group} /> : null}
                    </div>
                </div>

                <Section title="Trusted professionals" icon={Users}>
                    <Pros pros={data.professionals || {}} />
                </Section>

                <Section title="Emergency contacts" icon={Phone}>
                    {(data.emergency_contacts || []).length === 0 ? <Empty msg="No emergency contacts on file." /> : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.emergency_contacts.map((c, i) => (
                                <Card key={i}>
                                    <div className="font-display text-lg font-bold">{c.name}</div>
                                    <div className="text-xs text-muted-foreground capitalize">{c.relation}</div>
                                    <a href={`tel:${c.phone}`} className="mt-3 block font-mono text-primary text-base">{c.phone}</a>
                                </Card>
                            ))}
                        </div>
                    )}
                </Section>

                <Section title="Insurance policies" icon={ShieldCheck}>
                    {(data.policies || []).length === 0 ? <Empty msg="No insurance on file." /> : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.policies.map((p) => (
                                <Card key={p.id}>
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{p.policy_type}</div>
                                    <div className="font-display text-lg font-bold mt-1">{p.provider}</div>
                                    <div className="font-mono text-xs text-muted-foreground">#{p.policy_number}</div>
                                    <div className="mt-3 text-sm">Sum: <span className="font-mono">₹{Number(p.sum_assured || 0).toLocaleString("en-IN")}</span></div>
                                    <div className="text-sm">
                                        Nominee: <span className="font-semibold">{p.nominee || "—"}</span>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </Section>

                <Section title="Bank accounts & FDs" icon={Landmark}>
                    {(data.accounts || []).length === 0 ? <Empty msg="No accounts on file." /> : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.accounts.map((a) => (
                                <Card key={a.id}>
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{a.account_type.replace("_", " ")}</div>
                                    <div className="font-display text-lg font-bold mt-1">{a.bank_name}</div>
                                    {a.last4 ? <div className="font-mono text-xs text-muted-foreground">**** {a.last4}</div> : null}
                                </Card>
                            ))}
                        </div>
                    )}
                </Section>

                <Section title="Legal & legacy" icon={Scroll}>
                    {(data.legal || []).length === 0 ? <Empty msg="No legal docs on file." /> : (
                        <div className="grid sm:grid-cols-2 gap-4">
                            {data.legal.map((l) => (
                                <Card key={l.id}>
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{(l.legal_type || "").replace("_", " ")}</div>
                                    <div className="font-display text-lg font-bold mt-1">{l.title}</div>
                                    {l.location ? <div className="text-sm text-muted-foreground mt-2">Located: {l.location}</div> : null}
                                </Card>
                            ))}
                        </div>
                    )}
                </Section>

                <Section title="Assets" icon={Home}>
                    {(data.assets || []).length === 0 ? <Empty msg="No assets on file." /> : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.assets.map((a) => (
                                <Card key={a.id}>
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{a.asset_type}</div>
                                    <div className="font-display text-lg font-bold mt-1">{a.name}</div>
                                </Card>
                            ))}
                        </div>
                    )}
                </Section>

                <Section title="Key documents" icon={FileText}>
                    {(data.key_documents || []).length === 0 ? <Empty msg="No key documents tagged." /> : (
                        <ul className="rounded-lg border border-border divide-y divide-border bg-card">
                            {data.key_documents.map((d) => (
                                <li key={d.id} className="px-5 py-3 flex items-center justify-between">
                                    <span className="text-sm font-semibold">{d.name}</span>
                                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{d.category}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Section>
                <p className="text-xs text-muted-foreground">
                    This is a read-only view shared by the vault owner. Always cross-verify with originals.
                </p>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section>
            <div className="flex items-center gap-3 mb-4">
                <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
            </div>
            {children}
        </section>
    );
}
function Card({ children }) {
    return <div className="rounded-lg border border-border bg-card p-5">{children}</div>;
}
function Empty({ msg }) {
    return <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">{msg}</div>;
}
function Item({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <span className="text-muted-foreground">{label}:</span>
            <span className="font-semibold">{value}</span>
        </div>
    );
}
function Pros({ pros }) {
    const items = [
        ["Advisor", pros.advisor_name, pros.advisor_phone],
        ["CA", pros.ca_name, pros.ca_phone],
        ["Doctor", pros.doctor_name, pros.doctor_phone],
        ["Broker / Lawyer", pros.broker_name, pros.broker_phone],
    ].filter(([, n]) => n);
    if (!items.length) return <Empty msg="No professionals listed yet." />;
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map(([role, name, phone], i) => (
                <Card key={i}>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">{role}</div>
                    <div className="font-display text-lg font-bold mt-1">{name}</div>
                    {phone ? <a href={`tel:${phone}`} className="font-mono text-primary text-sm">{phone}</a> : null}
                </Card>
            ))}
        </div>
    );
}
